import { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setRedirectUrl } from "../redux/reducers/authReducer";
import RouteLoading from "../components/CommonComponents/RouteLoading";

/** Matches API-style email links: myrequirements/requirement-detail/:id (also "requirement detail" if encoded with a space). */
const VALID_SLUGS = new Set(["requirement-detail", "requirement detail"]);

/** Same raw-query read as ProtectedJobRoute / NewLoginVerificationCode so "+" in email is preserved. */
function readEmailFromSearch(search) {
  const m = search.match(/[?&]email=([^&]*)/);
  if (!m) return null;
  const v = decodeURIComponent(m[1]).trim();
  return v || null;
}

/**
 * Deep link from email → in-app requirements view with sidebar open for that id.
 * No token: store redirect and send to login (or verification-code login when ?email= is present — prefilled/disabled there).
 * With token: replace URL with /dashboard/requirements?active_id=…
 */
export default function RequirementEmailLinkGate() {
  const { slug, id } = useParams();
  const location = useLocation();
  const token = useSelector((state) => state.auth?.token);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const slugNorm = typeof slug === "string" ? slug.trim() : "";
    const idStr = typeof id === "string" ? id.trim() : "";
    if (!slugNorm || !idStr || !VALID_SLUGS.has(slugNorm) || !/^\d+$/.test(idStr)) {
      navigate("/pagenotfound", { replace: true });
      return;
    }
    const postLoginPath = `/dashboard/requirements?active_id=${idStr}`;
    const email = readEmailFromSearch(location.search);

    if (!token) {
      dispatch(setRedirectUrl(postLoginPath));
      if (email) {
        const verificationSearch = new URLSearchParams();
        verificationSearch.set("email", email);
        navigate(`/new-login-with-verification-code?${verificationSearch.toString()}`, {
          replace: true,
          state: {
            redirectTo: postLoginPath,
            from: location,
          },
        });
      } else {
        navigate("/login", { replace: true, state: { redirectTo: postLoginPath } });
      }
      return;
    }
    navigate(postLoginPath, { replace: true });
  }, [token, slug, id, location, dispatch, navigate]);

  return <RouteLoading />;
}
